import { createContext } from "react";

const getRelativeSize = ({ baseSize = 16, size }: { baseSize?: number; size: number }): string =>
    baseSize * size + "px";

const tokens = {
    global: {
        outline: null,
        outlineOffset: getRelativeSize({ size: 0.125 }),
    },

    // https://colornamer.robertcooper.me/
    colors: {
        white: "#FFFFFF",
        black: "#000",

        raven: "#0B0B0B",
        iron: "#5D5E5E",
        beluga: "#F0F2F2",
        toryRed: "#D76C61",
        strawberryRose: "#E39890",
        ivoryTower: "#fbf0ef",
        raceCarStripe: "#CD4942",
        doubleClick: "#CFD1D1",
        iceBlue: "#709BCD",

        carbonFiber: "#2D2D2D",
        coveredInPlatinum: "#B8BABA",
        silentDelight: "#E6E8E8",

        aquaVelvet: "#00A39E",
        blueWingedTeal: "#00807B",
        tidalPool: "#005D58",

        jupiter: "#E1E1E1",
        noghreiSilver: "#bdbdbd",
        moreThanWeek: "#8B8C8C",
        sweetPink: "#EE928D",
        blackTie: "#474747",
        thunderstormBlue: "#005063",
    },

    fonts: {
        families: {
            lato: "Lato, sans-serif",
        },

        sizes: {
            xs: getRelativeSize({ size: 0.75 }), // 12px
            sm: getRelativeSize({ size: 0.875 }), // 14px
            base: getRelativeSize({ size: 1 }), // 16px
        },

        weights: {
            light: 300,
            regular: 400,
            medium: 500,
            bold: 700,
        },
    },

    borders: {
        width: {
            md: getRelativeSize({ size: 0.125 }), // 2px
            base: getRelativeSize({ size: 0.0625 }), // 1px
        },

        radiuses: {
            sm: getRelativeSize({ size: 0.125 }), // 2px
            base: getRelativeSize({ size: 0.25 }), // 4px
            md: getRelativeSize({ size: 0.375 }), // 7px
            lg: getRelativeSize({ size: 0.5 }), // 8px
            xl: getRelativeSize({ size: 0.75 }), // 12px
            xxl: getRelativeSize({ size: 1 }), // 16px
            xxxl: getRelativeSize({ size: 1.5 }), // 24px
        },
    },

    spacings: {
        s1: getRelativeSize({ size: 0.0625 }), // 1px
        s2: getRelativeSize({ size: 0.125 }), // 2px
        s3: getRelativeSize({ size: 0.1875 }), // 3px
        s4: getRelativeSize({ size: 0.25 }), // 4px
        s5: getRelativeSize({ size: 0.3125 }), // 5px
        s6: getRelativeSize({ size: 0.375 }), // 6px
        s7: getRelativeSize({ size: 0.4375 }), // 7px
        s8: getRelativeSize({ size: 0.5 }), // 8px
        s9: getRelativeSize({ size: 0.5625 }), // 9px
        s10: getRelativeSize({ size: 0.625 }), // 10px
        s11: getRelativeSize({ size: 0.6875 }), // 11px
        s12: getRelativeSize({ size: 0.75 }), // 12px
        s13: getRelativeSize({ size: 0.8125 }), // 13px
        s14: getRelativeSize({ size: 0.875 }), // 14px
        s15: getRelativeSize({ size: 0.9375 }), // 15px
        s16: getRelativeSize({ size: 1 }), // 16px
        s17: getRelativeSize({ size: 1.0625 }), // 17px
        s18: getRelativeSize({ size: 1.125 }), // 18px
        s19: getRelativeSize({ size: 1.1875 }), // 19px
        s20: getRelativeSize({ size: 1.25 }), // 20px
        s21: getRelativeSize({ size: 1.3125 }), // 21px
        s22: getRelativeSize({ size: 1.375 }), // 22px
        s23: getRelativeSize({ size: 1.4375 }), // 23px
        s24: getRelativeSize({ size: 1.5 }), // 24px
    },
};

tokens.global.outline = `${tokens.borders.width.md} solid ${tokens.colors.iceBlue}`;

export const themeContextNuO = {
    themes: {
        light: {
            tokens,

            button: {
                common: {
                    fontFamily: tokens.fonts.families.lato,
                    fontSize: tokens.fonts.sizes.sm,
                    fontWeight: tokens.fonts.weights.bold,
                    borderRadius: tokens.borders.radiuses.base,

                    focus: {
                        outline: tokens.global.outline,
                        outlineOffset: tokens.global.outlineOffset,
                    },
                },
                default: {
                    color: tokens.colors.carbonFiber,
                    borderColor: tokens.colors.coveredInPlatinum,
                    backgroundColor: tokens.colors.white,

                    hover: {
                        color: tokens.colors.carbonFiber,
                        borderColor: tokens.colors.coveredInPlatinum,
                        backgroundColor: tokens.colors.beluga,
                    },

                    active: {
                        color: tokens.colors.carbonFiber,
                        borderColor: tokens.colors.coveredInPlatinum,
                        backgroundColor: tokens.colors.silentDelight,
                    },

                    disabled: {
                        color: tokens.colors.coveredInPlatinum,
                        backgroundColor: tokens.colors.beluga,
                        borderColor: tokens.colors.beluga,
                    },
                },
                primary: {
                    color: tokens.colors.white,
                    borderColor: tokens.colors.aquaVelvet,
                    backgroundColor: tokens.colors.aquaVelvet,

                    hover: {
                        color: tokens.colors.white,
                        borderColor: tokens.colors.blueWingedTeal,
                        backgroundColor: tokens.colors.blueWingedTeal,
                    },

                    active: {
                        color: tokens.colors.white,
                        borderColor: tokens.colors.tidalPool,
                        backgroundColor: tokens.colors.tidalPool,
                    },

                    disabled: {
                        color: tokens.colors.coveredInPlatinum,
                        backgroundColor: tokens.colors.beluga,
                        borderColor: tokens.colors.beluga,
                    },
                },
                action: {},
                subtle: {},
            },

            loadingIndicator: {
                default: {
                    color: tokens.colors.carbonFiber,
                },
                primary: {
                    color: tokens.colors.aquaVelvet,
                },
            },

            tabs: {
                primary: {
                    borderColor: tokens.colors.jupiter,

                    activeTab: {
                        borderColor: tokens.colors.aquaVelvet,
                    },
                },
            },
            tab: {
                common: {
                    fontFamily: tokens.fonts.families.lato,
                    fontSize: tokens.fonts.sizes.sm,
                    fontWeight: tokens.fonts.weights.regular,
                    borderRadius: tokens.borders.radiuses.base,

                    focus: {
                        outline: tokens.global.outline,
                        outlineOffset: `${parseInt(tokens.global.outlineOffset, 10) + 2}px`,
                    },
                },
                primary: {
                    color: tokens.colors.carbonFiber,
                    backgroundColor: tokens.colors.white,

                    selected: {
                        fontWeight: tokens.fonts.weights.bold,
                    },

                    disabled: {
                        color: tokens.colors.iron,
                    },

                    hover: {
                        backgroundColor: tokens.colors.beluga,
                    },

                    active: {
                        backgroundColor: tokens.colors.silentDelight,
                    },
                },
            },

            select: {
                common: {
                    color: tokens.colors.carbonFiber,
                    fontFamily: tokens.fonts.families.lato,
                    fontSize: tokens.fonts.sizes.sm,
                    fontWeight: tokens.fonts.weights.regular,

                    placeholderColor: tokens.colors.moreThanWeek,

                    borderRadius: tokens.borders.radiuses.base,
                    borderColor: tokens.colors.coveredInPlatinum,

                    offset: tokens.spacings.s4,

                    iconColor: tokens.colors.blackTie,

                    focus: {
                        outline: tokens.global.outline,
                        outlineOffset: tokens.global.outlineOffset,
                    },
                },

                startIcon: {
                    offset: tokens.spacings.s8,
                },

                error: {
                    color: tokens.colors.raceCarStripe,
                },

                inputError: {
                    color: tokens.colors.strawberryRose,
                    backgroundColor: tokens.colors.ivoryTower,
                    border: tokens.colors.sweetPink,
                },

                option: {
                    color: tokens.colors.carbonFiber,
                    selectedBackgroundColor: tokens.colors.silentDelight,
                    focuedBackgroundColor: tokens.colors.beluga,
                },
            },

            avatar: {
                common: {
                    fontFamily: tokens.fonts.families.lato,
                    fontSize: tokens.fonts.sizes.sm,
                    fontWeight: tokens.fonts.weights.regular,

                    borderRadius: tokens.borders.radiuses.base,

                    color: tokens.colors.white,
                    backgroundColor: tokens.colors.thunderstormBlue,
                },
            },
        },
    },
    currentTheme: "light",
};

export const ThemeContextNuO = createContext(null);
